import { Collapse, Typography } from 'antd';
import { Link } from 'react-router';
import ReactMarkdown from 'react-markdown';
import { space } from '@viq-admin/ui';
import assembly from './sections/assembly.md?raw';
import output from './sections/output.md?raw';
import writing from './sections/writing.md?raw';

/**
 * Collapsible reference panels for the prompt-authoring screens.
 *
 * The same markdown sections are rendered by the AI Prompt Guide page, so there
 * is one source for this content and no drift between the guide and the screens.
 * Collapsed by default: this is material you open while authoring, not every
 * time you visit.
 *
 * `sections` lets each screen show only what is relevant — the templates screen
 * has no use for core-prompt writing advice beyond how appends are wrapped.
 */

export type PromptReferenceSection = 'assembly' | 'output' | 'writing';

const SECTIONS: Record<PromptReferenceSection, { label: string; body: string }> = {
  assembly: { label: 'How the full prompt is assembled', body: assembly },
  output: { label: 'What the AI returns', body: output },
  writing: { label: 'Writing a good prompt', body: writing },
};

interface Props {
  sections: PromptReferenceSection[];
}

export function PromptReference({ sections }: Props) {
  return (
    <div data-testid="prompt-reference" style={{ marginBottom: space.lg }}>
      <Collapse
        ghost
        items={sections.map((key) => ({
          key,
          label: SECTIONS[key].label,
          children: (
            <Typography style={{ maxWidth: 760 }}>
              <ReactMarkdown>{SECTIONS[key].body}</ReactMarkdown>
            </Typography>
          ),
        }))}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: space.xs }}>
        <Link to="/prompt-guide">Full AI Prompt Guide</Link>
      </Typography.Paragraph>
    </div>
  );
}
